import React, { useContext, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate
} from 'react-router-dom';
import { AuthContext } from './contexts/AuthContext';
import "@calcom/atoms/globals.min.css";
import { CalProvider } from '@calcom/atoms';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

  
// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Lazy load all pages to improve initial load performance
const NotFound = React.lazy(() => import('./pages/NotFound'));
const Index = React.lazy(() => import('./pages/Index'));
const PublicPresentation = React.lazy(() => import('./pages/PublicPresentation'));
const CalComCallback = React.lazy(() => import('./pages/CalComCallback'));
const PendingApprovalMessage = React.lazy(() => import('./components/PendingApprovalMessage'));

// Doctor pages - lazy loaded
const DoctorDashboard = React.lazy(() => import('./pages/DoctorDashboard'));
const DoctorCalendar = React.lazy(() => import('./pages/DoctorCalendar'));
const DoctorPatients = React.lazy(() => import('./pages/DoctorPatients'));
const DoctorProfile = React.lazy(() => import('./pages/DoctorProfile'));
const DoctorAssessment = React.lazy(() => import('./pages/DoctorAssessment'));
const DoctorAssessmentDetails = React.lazy(() => import('./pages/DoctorAssessmentDetails'));
const DoctorDocuments = React.lazy(() => import('./pages/DoctorDocuments'));
const DoctorPresentationTemplates = React.lazy(() => import('./pages/DoctorPresentationTemplatesV2'));
const ControlPanel = React.lazy(() => import('./pages/ControlPanel'));
const ControlPanelLegacy = React.lazy(() => import('./pages/ControlPanelLegacy'));
const PatientDetail = React.lazy(() => import('./pages/PatientDetail'));
const SurgeryRequestPage = React.lazy(() => import('./pages/SurgeryRequestPage'));
const AdminDoctorApprovals = React.lazy(() => import('./pages/AdminDoctorApprovals'));

// Surgery Flow Pages
const SurgeryMainPage = React.lazy(() => import('./pages/SurgeryMainPage'));
const SurgicalRequestDetails = React.lazy(() => import('./pages/SurgicalRequestDetails'));
const PostOperativeDetails = React.lazy(() => import('./pages/PostOperativeDetails'));

// Password Management
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const ChangePassword = React.lazy(() => import('./pages/ChangePassword'));

// Patient pages - lazy loaded
const PatientDashboard = React.lazy(() => import('./pages/PatientDashboard'));
const PatientCalendar = React.lazy(() => import('./pages/PatientCalendar'));
const PatientAssessment = React.lazy(() => import('./pages/PatientAssessment'));
const PatientAssessmentDetails = React.lazy(() => import('./pages/PatientAssessmentDetails'));
const PatientAssessmentsList = React.lazy(() => import('./pages/PatientAssessmentsList'));
const PatientPresentationsList = React.lazy(() => import('./pages/PatientPresentationsList'));
const PatientPresentationsNew = React.lazy(() => import('./pages/PatientPresentationsNew'));
const PatientPresentationDetail = React.lazy(() => import('./pages/PatientPresentationDetail'));
const PatientStatisticsList = React.lazy(() => import('./pages/PatientStatisticsList'));
const PatientDailySymptoms = React.lazy(() => import('./pages/PatientDailySymptoms'));
const PatientDoctorChat = React.lazy(() => import('./pages/PatientDoctorChat'));

// Doctor Messages
const DoctorMessages = React.lazy(() => import('./pages/DoctorMessages'));

// Assistente Management
const AssistenteSignup = React.lazy(() => import('./pages/AssistenteSignup'));

// AI Assistant - lazy loaded
const DoctorAIAssistant = React.lazy(() => import('./pages/DoctorAIAssistant'));

// Legal pages
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./pages/TermsOfService'));

// Beta Landing Page
const LandingPageBeta = React.lazy(() => import('./pages/LandingPageBeta'));


// Error boundary component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode, fallback: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode, fallback: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: any) {
    console.error("App error caught by ErrorBoundary:", error);
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Error details:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

function App({ Component, pageProps }) {
  const { user, userType, isLoading, hasValidSession, isApproved, isSuperAdmin, isInitialized } = useContext(AuthContext);

  const requireSuperAdmin = !isSuperAdmin  // Private route wrapper component
  const PrivateRoute = ({ children, requiredUserType }: { children: React.ReactNode,  requiredUserType?: 'medico' | 'paciente' | 'assistente',  }) => {
    // Don't render anything while auth is loading or initializing
    if (isLoading || !isInitialized) return null;
    
    // Redirect to login if not authenticated
    if (!user || !hasValidSession) {
      console.log('PrivateRoute: No valid session, redirecting to home');
      return <Navigate to="/" replace />;
    }
    
    // If a specific user type is required, check that too
    if (requiredUserType && userType !== requiredUserType) {
      let redirectPath = '/';
      if (userType === 'medico') redirectPath = '/medico/dashboard';
      else if (userType === 'paciente') redirectPath = '/paciente/dashboard';
      else if (userType === 'assistente') redirectPath = '/assistente/dashboard';
      console.log(`PrivateRoute: User type mismatch, redirecting to ${redirectPath}`);
      return <Navigate to={redirectPath} replace />;
    }
    
    if ((requiredUserType === 'medico' || requiredUserType === 'assistente') && !isApproved) {
      console.log('PrivateRoute: Doctor/Assistente not approved, redirecting to pending approval');
      return <Navigate to="/pending-approval" replace />;
    }
    
    // User is authenticated and has the right type, render the children
    return <>{children}</>;
  };

  // Public route wrapper component - redirects to dashboard if already logged in
  const PublicRoute = ({ children }: { children: React.ReactNode }) => {
    // Don't render anything while auth is loading
    if (isLoading) return null;
    
    if (user && hasValidSession && userType) {
      let redirectPath = '/';
      if (userType === 'medico') redirectPath = '/medico/dashboard';
      else if (userType === 'paciente') redirectPath = '/paciente/dashboard';
      else if (userType === 'assistente') redirectPath = '/assistente/dashboard';
      return <Navigate to={redirectPath} replace />;
    }
    
    return <>{children}</>;
  };

  // Auth reset routes - never redirect, even if not logged in
  const AuthResetRoute = ({ children }: { children: React.ReactNode }) => {
    // Render normally regardless of auth status - needed for password reset flows
    return <>{children}</>;
  };

  // Loading fallback component
  const PageLoading = () => (
    <div className="flex items-center justify-center h-screen w-full bg-darkblue-900">
      <div className="flex flex-col items-center">
        <div className="w-16 h-16 rounded-full border-4 border-gold-500/20 border-t-gold-500 animate-spin mb-4"></div>
        <p className="text-gold-400 font-medium">Carregando...</p>
      </div>
    </div>
  );
  
  // Suspense wrapper for lazy-loaded components
  const LazyRoute = ({ children }: { children: React.ReactNode }) => (
    <Suspense fallback={<PageLoading />}>
      {children}
    </Suspense>
  );

  // Main router content that works without Cal.com
  const routerContent = (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<PublicRoute><LazyRoute><Index /></LazyRoute></PublicRoute>} />
        <Route path="/login" element={<PublicRoute><LazyRoute><Index /></LazyRoute></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><LazyRoute><Index /></LazyRoute></PublicRoute>} />
        
        {/* Legal Pages - Public Access */}
        <Route path="/privacy-policy" element={<LazyRoute><PrivacyPolicy /></LazyRoute>} />
        <Route path="/terms-of-service" element={<LazyRoute><TermsOfService /></LazyRoute>} />
        
        {/* Beta Landing Page - Public Access */}
        <Route path="/beta" element={<LazyRoute><LandingPageBeta /></LazyRoute>} />

        {/* Public shared presentation - no auth, token in URL */}
        <Route path="/resumo/:token" element={<LazyRoute><PublicPresentation /></LazyRoute>} />
        <Route path="/resumo" element={<LazyRoute><PublicPresentation /></LazyRoute>} />

        {/* Doctor Routes */}
        <Route path="/medico/dashboard" element={<PrivateRoute requiredUserType="medico"><LazyRoute><DoctorDashboard /></LazyRoute></PrivateRoute>} />
        
        {/* Unify calendar routes - both /calendario and /agenda point to the same component */}
        <Route path="/medico/calendario" element={<PrivateRoute requiredUserType="medico"><LazyRoute><DoctorCalendar /></LazyRoute></PrivateRoute>} />
        <Route path="/medico/agenda" element={<Navigate to="/medico/calendario" replace />} />

        <Route path="/medico/pacientes" element={<PrivateRoute requiredUserType="medico"><LazyRoute><DoctorPatients /></LazyRoute></PrivateRoute>} />
        <Route path="/medico/paciente/:prontuarioId" element={<PrivateRoute requiredUserType="medico"><LazyRoute><PatientDetail /></LazyRoute></PrivateRoute>} />
        <Route path="/medico/perfil" element={<PrivateRoute requiredUserType="medico"><LazyRoute><DoctorProfile /></LazyRoute></PrivateRoute>} />
        <Route path="/medico/avaliacao" element={<PrivateRoute requiredUserType="medico"><LazyRoute><DoctorAssessment /></LazyRoute></PrivateRoute>} />
        <Route path="/medico/avaliacao/:id" element={<PrivateRoute requiredUserType="medico"><LazyRoute><DoctorAssessmentDetails /></LazyRoute></PrivateRoute>} />
        <Route path="/medico/documentos" element={<PrivateRoute requiredUserType="medico"><LazyRoute><DoctorDocuments /></LazyRoute></PrivateRoute>} />
        <Route path="/medico/apresentacoes" element={<PrivateRoute requiredUserType="medico"><LazyRoute><DoctorPresentationTemplates /></LazyRoute></PrivateRoute>} />
        {/* Main Surgery Page - contains all surgery features in tabs */}
        <Route path="/medico/cirurgias" element={<PrivateRoute requiredUserType="medico"><LazyRoute><SurgeryMainPage /></LazyRoute></PrivateRoute>} />
        <Route path="/medico/cirurgias/:id" element={<PrivateRoute requiredUserType="medico"><LazyRoute><SurgicalRequestDetails /></LazyRoute></PrivateRoute>} />
        <Route path="/medico/cirurgias/:id/edit" element={<PrivateRoute requiredUserType="medico"><LazyRoute><SurgicalRequestDetails /></LazyRoute></PrivateRoute>} />
        <Route path="/medico/pos-operatorio/:id" element={<PrivateRoute requiredUserType="medico"><LazyRoute><PostOperativeDetails /></LazyRoute></PrivateRoute>} />
        
        {/* Legacy route redirect for pedidos-cirurgia */}
        <Route path="/medico/pedidos-cirurgia" element={<Navigate to="/medico/cirurgias" replace />} />
        
        <Route path="/medico/painel-controle" element={<PrivateRoute requiredUserType="medico"><LazyRoute><ControlPanel /></LazyRoute></PrivateRoute>} />
        <Route path="/medico/painel-antigo" element={<PrivateRoute requiredUserType="medico"><LazyRoute><ControlPanelLegacy /></LazyRoute></PrivateRoute>} />
        <Route path="/medico/aprovacoes" element={<PrivateRoute requiredUserType="medico"><LazyRoute><AdminDoctorApprovals /></LazyRoute></PrivateRoute>} />
        <Route path="/medico/mensagens" element={<PrivateRoute requiredUserType="medico"><LazyRoute><DoctorMessages /></LazyRoute></PrivateRoute>} />
        <Route path="/medico/assistentes" element={<PrivateRoute requiredUserType="medico"><LazyRoute><AssistenteSignup /></LazyRoute></PrivateRoute>} />
        <Route path="/medico/assistente-ia" element={<PrivateRoute requiredUserType="medico"><LazyRoute><DoctorAIAssistant /></LazyRoute></PrivateRoute>} />
        

        {/* Patient Routes */}
        <Route path="/paciente/dashboard" element={<PrivateRoute requiredUserType="paciente"><LazyRoute><PatientDashboard /></LazyRoute></PrivateRoute>} />
        
        {/* Unify patient calendar routes */}
        <Route path="/paciente/calendario" element={<PrivateRoute requiredUserType="paciente"><LazyRoute><PatientCalendar /></LazyRoute></PrivateRoute>} />
        <Route path="/paciente/agenda" element={<Navigate to="/paciente/calendario" replace />} />
        
        {/* Explicitly refer to Doctor assessment route if user is actually a doctor */}
        <Route path="/paciente/avaliacao" element={
          <Suspense fallback={<PageLoading />}>
            {userType === 'medico' ? 
              <Navigate to="/medico/avaliacao" replace /> : 
              <PrivateRoute requiredUserType="paciente"><PatientAssessment /></PrivateRoute>
            }
          </Suspense>
        } />
        <Route path="/paciente/avaliacoes" element={<PrivateRoute requiredUserType="paciente"><LazyRoute><PatientAssessmentsList /></LazyRoute></PrivateRoute>} />
        <Route path="/paciente/avaliacao/:id" element={<PrivateRoute requiredUserType="paciente"><LazyRoute><PatientAssessmentDetails /></LazyRoute></PrivateRoute>} />
        <Route path="/paciente/avaliacoes/:id" element={<PrivateRoute requiredUserType="paciente"><LazyRoute><PatientAssessmentDetails /></LazyRoute></PrivateRoute>} />
        <Route path="/paciente/apresentacoes" element={<PrivateRoute requiredUserType="paciente"><LazyRoute><PatientPresentationsNew /></LazyRoute></PrivateRoute>} />
        <Route path="/paciente/apresentacoes/:id" element={<PrivateRoute requiredUserType="paciente"><LazyRoute><PatientPresentationDetail /></LazyRoute></PrivateRoute>} />
        <Route path="/paciente/estatisticas" element={<PrivateRoute requiredUserType="paciente"><LazyRoute><PatientStatisticsList /></LazyRoute></PrivateRoute>} />
        
        {/* New Patient Routes */}
        <Route path="/paciente/sintomas" element={<PrivateRoute requiredUserType="paciente"><LazyRoute><PatientDailySymptoms /></LazyRoute></PrivateRoute>} />
        <Route path="/paciente/mensagens" element={<PrivateRoute requiredUserType="paciente"><LazyRoute><PatientDoctorChat /></LazyRoute></PrivateRoute>} />
        
        {/* Assistente Routes */}
        <Route path="/assistente/dashboard" element={<Navigate to="/assistente/cirurgias" replace />} />
        <Route path="/assistente/cirurgias" element={<PrivateRoute requiredUserType="assistente"><LazyRoute><SurgeryMainPage /></LazyRoute></PrivateRoute>} />
        <Route path="/assistente/cirurgias/:id" element={<PrivateRoute requiredUserType="assistente"><LazyRoute><SurgicalRequestDetails /></LazyRoute></PrivateRoute>} />
        <Route path="/assistente/pos-operatorio/:id" element={<PrivateRoute requiredUserType="assistente"><LazyRoute><PostOperativeDetails /></LazyRoute></PrivateRoute>} />
        
        {/* Redirect root doctor/patient/assistente paths to their dashboards */}
        <Route path="/medico" element={<Navigate to="/medico/dashboard" replace />} />
        <Route path="/paciente" element={<Navigate to="/paciente/dashboard" replace />} />
        <Route path="/assistente" element={<Navigate to="/assistente/dashboard" replace />} />
        
        {/* Special Routes */}
        <Route path="/calcom/callback" element={<LazyRoute><CalComCallback /></LazyRoute>} />
        <Route path="/pending-approval" element={<LazyRoute><PendingApprovalMessage /></LazyRoute>} />
        
        {/* Password Management Routes */}
        <Route path="/forgot-password" element={<AuthResetRoute><LazyRoute><ForgotPassword /></LazyRoute></AuthResetRoute>} />
        <Route path="/reset-password" element={<AuthResetRoute><LazyRoute><ResetPassword /></LazyRoute></AuthResetRoute>} />
        <Route path="/change-password" element={<LazyRoute><ChangePassword /></LazyRoute>} />
        
        {/* Catch-all route for 404 */}
        <Route path="*" element={<LazyRoute><NotFound /></LazyRoute>} />
      </Routes>
    </Router>
  );

  // Final render - wrap routerContent with CalProvider
  return (
    <QueryClientProvider client={queryClient}>
      <CalProvider
        clientId={process.env.VITE_CAL_COM_CLIENT_ID ?? ""}
        options={{
          apiUrl: "https://api.cal.com",
        }}
      >
        {routerContent}
      </CalProvider>
    </QueryClientProvider>
  );
}

export default App;
